import type {LoaderFunctionArgs, MetaFunction} from 'react-router'
import {useLoaderData, useRouteLoaderData} from 'react-router'
import {mergeMeta} from '@core/lib/mergeMeta'
import type {OwnerRun} from '@core/packs/dSubState'
import {getCatalogView, getLivePack} from '@product/narcis/packs/catalog'
import {buildPackMeta} from '@product/narcis/packs/seo'
import {PackLandingTemplate} from '@product/narcis/packs/PackLandingTemplate'
import {resolveRunStatus} from '@product/narcis/packs/packRun.server'
import {packInstanceKey} from '@product/narcis/packs/packInstanceKey'
import {NARCIS_GENERATIONS_BASE_URL} from '@product/narcis/config'
import {readTilesByPackSlug} from '@core/wall/tiles.server'
import type {loader as rootLoader} from '@root'

// The owner-run action (narcis_funnel plan §9, 2026-07-25 settlement — start-run/regenerate via
// `ensureRunGrant`/`driveGrantRun`) is RETIRED here (narcis wall plan step 6, regenerate migration,
// 2026-09-24). `start-run` had no live caller left once the D-state first render moved to the wall
// (`PackLandingTemplate.tsx`'s `fireWallPackContinue`, 2026-09-22); `regenerate` was the last one and
// now shares that same wall write (`handleRegenerate` → `fireWallPackContinue('regenerate')`, that
// file's own doc). With both intents gone this route has no action left to export — `packRun.server.ts`
// (`ensureRunGrant`/`driveGrantRun`) and the pack-run poller stay for Ovid, which still drives its own
// pack page through them (see that product's `PackLandingTemplate.tsx`), and to let any grant run still
// in flight at deploy time finish.

export async function loader({request, params}: LoaderFunctionArgs) {
    const pack = getLivePack(params.packSlug!)
    if (!pack) throw new Response('Not Found', {status: 404})

    // The wall tile behind each look the stage shows, in the stage's own order (`pack.rows`
    // flattened) — the look door's destination (plan 2026_09_25_narcis_pack_coda.md).
    // `wall_tiles.position` is the cell's index in `samples.images[]` (`scripts/wall/
    // ingest_narcis.ts`), so each look is matched to its position by URL. `lookTileIds` stays
    // all-null when the wall has not ingested this pack, and `cellsTravel` then shows no door.
    const wallTiles = await readTilesByPackSlug('narcis', pack.slug)
    const tileIdAtPosition = new Map(wallTiles.map((t) => [t.position, t.id] as const))
    const lookTileIds = pack.rows.flatMap((r) => r.portraits).map((src) => {
        const position = pack.samples.images.findIndex((img) => img.src === src)
        return tileIdAtPosition.get(position) ?? null
    })

    const {getSession} = await import('@core/auth/session.server')
    const {prisma} = await import('@core/database/prisma.server')

    const session = await getSession(request.headers.get('cookie'))
    const userId = session?.data?.userId as string | undefined

    // Owner run state (narcis_funnel plan §9) — the SINGLE delivered-rendering-path resolution.
    // `?id=` (auth-gated: silently ignored if the grant isn't this user's) deep-links to a
    // SPECIFIC historical grant — the mechanism that keeps a re-rendered pack's prior nine
    // reachable from My Packs. Its absence resolves to the most recent grant for this pack, which
    // is exactly "the current run" the plain `/{slug}` URL always shows. No grant at all for this
    // (user, pack) → ownerRun stays null → pack page state D (never started).
    const grantIdParam = new URL(request.url).searchParams.get('id') ?? undefined
    let ownerRun: OwnerRun | null = null

    if (userId) {
        const {resolveDeliveredPackPhotos} = await import('@product/narcis/packs/deliveredPhotos.server')
        const photos = await resolveDeliveredPackPhotos(userId, pack.slug, grantIdParam)
        if (photos) {
            // Presence stamp — MOVED (content/state split plan, Phase 1 step 4) to
            // `api.pack.run-status.tsx`'s `?grant=` branch, which now fires on every D-gen poll
            // tick (`PackLandingTemplate.tsx`'s re-attach poll, including an immediate fetch on
            // mount) instead of this loader. This loader hit is now a first-paint read only.

            // resolveRunStatus's 'none' case is unreachable here — `photos` is only non-null when
            // a grant exists (its own null-check already covers "no grant at all"). Narrowed
            // explicitly rather than widening ownerRun.status to include 'none'.
            const runStatus = resolveRunStatus(photos)
            ownerRun = {
                status: runStatus === 'none' ? 'in_progress' : runStatus,
                grantId: photos.grantId,
                batchId: photos.batchId,
                createdAt: photos.createdAt.toISOString(),
                boundCount: photos.boundCount,
                maxGenerations: photos.maxGenerations,
                slots: photos.slots.map((slot) => ({
                    slotIndex: slot.slotIndex,
                    imageId: slot.generationId,
                    imageUrl: slot.generationId ? `${NARCIS_GENERATIONS_BASE_URL}/${slot.generationId}.webp` : null,
                })),
            }
        }
    }

    // Returning-buyer banner: fetch most-recent completed grant for this user+pack.
    // Completed = bound-slot count >= maxGenerations (pack slot ledger, 2026-07-25 — see
    // packRun.server.ts's resolveRunStatus). Only relevant when no ?id= param (deliveredView
    // already shows the grant directly) and user is signed in.
    let recentCompletedGrant: {id: string; createdAt: Date} | null = null
    if (userId && !grantIdParam) {
        const grants = await prisma.packGrant.findMany({
            where: {userId, packSlug: pack.slug, product: 'narcis'},
            orderBy: {createdAt: 'desc'},
            take: 20,
            select: {id: true, createdAt: true, maxGenerations: true},
        })
        if (grants.length > 0) {
            const boundRows = await prisma.packConsumption.groupBy({
                by: ['grantId'],
                where: {grantId: {in: grants.map((g) => g.id)}, generationId: {not: null}},
                _count: {_all: true},
            })
            const boundByGrant = new Map(boundRows.map((r) => [r.grantId, r._count._all]))
            const completed = grants.find((g) => (boundByGrant.get(g.id) ?? 0) >= g.maxGenerations)
            if (completed) {
                recentCompletedGrant = {id: completed.id, createdAt: completed.createdAt}
            }
        }
    }

    // Drives the workbench's waiting_model stage (canvas 03) once a signed-in user has a model
    // that's still training, and the owner stages once it is promoted.
    const {getUserModel} = await import('@core/products/trainingShift.server')
    const userModel = userId ? await getUserModel(userId) : null

    // Catalog lookup for the detail-hero props (hubName, categoryLabel).
    const catalogView = getCatalogView()
    const catalogEntry = catalogView.livePacks.find((p) => p.slug === pack.slug)
    const categoryEntry = catalogView.categories.find((c) => c.id === catalogEntry?.cat)

    // Related strip (pack page copy spec §3, state A + state D; step 0's resolveRelatedTitle/
    // resolveRelatedPacks resolvers). Built here (not in the template) because it needs the
    // registry + catalog together — the template only ever receives serializable card data.
    const {resolveRelatedTitle, resolveRelatedPacks} = await import('@product/narcis/packs/relatedPacks')
    const relatedTitle = resolveRelatedTitle(pack)
    const relatedSlugs = resolveRelatedPacks(pack, catalogView)
    const relatedPacksCards = relatedSlugs
        .map((slug) => {
            const view = catalogView.livePacks.find((p) => p.slug === slug)
            const livePack = getLivePack(slug)
            if (!view || !livePack) return null
            const catLabel = catalogView.categories.find((c) => c.id === view.cat)?.label ?? view.cat
            return {
                slug: view.slug,
                title: view.name,
                cover: view.cover_portrait,
                nImages: livePack.grid.nImages,
                categoryLabel: catLabel,
            }
        })
        .filter((c): c is NonNullable<typeof c> => c !== null)

    return {
        pack,
        lookTileIds,
        hubName: catalogEntry?.name ?? pack.title,
        categoryLabel: categoryEntry?.label ?? '',
        category: catalogEntry?.cat ?? '',
        ownerRun,
        recentCompletedGrant: recentCompletedGrant
            ? {id: recentCompletedGrant.id, createdAt: recentCompletedGrant.createdAt.toISOString()}
            : null,
        userModel: userModel
            ? {...userModel, trainedAt: userModel.trainedAt.toISOString(), startedAt: userModel.startedAt?.toISOString()}
            : null,
        isAuthenticated: !!userId,
        relatedTitle,
        relatedPacksCards,
    }
}

export const meta: MetaFunction<typeof loader> = ({data, matches}) => {
    if (!data) return []
    return mergeMeta(matches, buildPackMeta(data.pack))
}

// What Start asks of this viewer is root's reading (`modelPayment`), not this page's: one source
// for the pack page, the wall and the intake.
export default function PackPage() {
    const {
        pack, lookTileIds, hubName, categoryLabel, category, ownerRun,
        recentCompletedGrant, userModel, isAuthenticated, relatedTitle, relatedPacksCards,
    } = useLoaderData<typeof loader>()
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const modelPayment = rootData?.modelPayment
    return (
        <PackLandingTemplate
            // Operator fix (DEVIATIONS.md #44): forces a remount on slug change so a related-strip
            // click never leaves the composer/stage rendering the previous pack's generation state
            // — see packInstanceKey.ts for the full mechanism.
            key={packInstanceKey(pack.slug)}
            pack={pack}
            lookTileIds={lookTileIds}
            hubName={hubName}
            categoryLabel={categoryLabel}
            category={category}
            asksPayment={modelPayment === 'pay'}
            currency={rootData?.modelCurrency}
            ownerRun={ownerRun ?? undefined}
            recentCompletedGrant={recentCompletedGrant ?? undefined}
            userModel={userModel ?? undefined}
            relatedTitle={relatedTitle}
            relatedPacksCards={relatedPacksCards}
        />
    )
}
